/* eslint-disable @typescript-eslint/no-explicit-any */
// ** React Imports
import { Link } from "react-router";
import { useEffect, useState } from "react";

// ** Custom Components
import Avatar from "@components/avatar";

// ** Store & Actions
import { useSelector } from "react-redux";
import { useAppDispatch } from "@src/redux/hooks";

// ** Third Party Components
import {
  User,
  Mail,
  CheckSquare,
  MessageSquare,
  Settings,
  CreditCard,
  HelpCircle,
  Power,
} from "react-feather";

// ** Reactstrap Imports
import {
  UncontrolledDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
} from "reactstrap";

import { getUserData } from "@src/utility/Utils";
import { clearAuth } from "@src/redux/auth";
import { buildImageUrl } from "@src/utility/urlHelper";
import { getUserDisplayName } from "@src/utility/userProfile";

const UserDropdown = () => {
  // ** Store Vars
  const dispatch = useAppDispatch();
  
  // ** Get user data from Redux (for real-time updates)
  const { user: reduxUser } = useSelector((state: any) => state.auth);

  // ** State
  const [avatarError, setAvatarError] = useState(false);
  
  // ** Use Redux data directly - updates when profile is saved
  const userData = reduxUser || getUserData();
  const displayName = getUserDisplayName(userData);

  useEffect(() => {
    setAvatarError(false);
  }, [userData?.profileImage, userData?.avatar, displayName]);

  const handleLogoutClick = () => {
    try {
      // Clears storage (fires `storage` in other tabs) + Redux auth
      dispatch(clearAuth());
      window.location.replace("/login");
    } catch (error) {
      console.error("❌ Error during logout:", error);
      localStorage.removeItem("userData");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.replace("/login");
    }
  };

  // ✅ Avatar logic with online indicator
  const getAvatarContent = (showOnlineStatus = false) => {
    const fullName = displayName;

    // Get profile image URL
    const profileImageUrl = userData?.profileImage || userData?.avatar;
    const imageUrl = profileImageUrl ? buildImageUrl(profileImageUrl) : null;

    const avatarContent =
      imageUrl && !avatarError ? (
        <img
          src={imageUrl}
          alt={fullName}
          className="rounded-circle"
          width="40"
          height="40"
          style={{ objectFit: "cover" }}
          onError={() => {
            console.warn("Avatar image failed, showing initials");
            setAvatarError(true);
          }}
          onLoad={() => {
            setAvatarError(false);
          }}
        />
      ) : (
        <Avatar
          initials
          color="light-primary"
          content={fullName}
          width="40"
          height="40"
        />
      );

    // If online status is needed, wrap in relative container
    if (showOnlineStatus) {
      return (
        <div className="position-relative">
          {avatarContent}
          <span
            className="position-absolute bg-success rounded-circle dropdown-user-online"
            style={{
              width: "10px",
              height: "10px",
              bottom: "2px",
              right: "2px",
            }}
          />
        </div>
      );
    }

    return avatarContent;
  };

  return (
    <UncontrolledDropdown tag="li" className="dropdown-user nav-item">
      <DropdownToggle
        href="/"
        tag="a"
        className="nav-link dropdown-user-link"
        onClick={(e) => e.preventDefault()}
      >
        {/* ✅ Avatar with online status in header */}
        {getAvatarContent(true)}
      </DropdownToggle>

      <DropdownMenu end className="dropdown-menu-end dropdown-user-menu">
        <div className="dropdown-user-header">
          <div className="me-2">{getAvatarContent(true)}</div>
          <div className="dropdown-user-info">
            <div className="dropdown-user-name">{displayName}</div>
            <small className="dropdown-user-role">
              {userData?.roleName || userData?.role || "Admin"}
            </small>
          </div>
        </div>

        <DropdownItem tag={Link} to="/pages/profile" className="dropdown-user-item">
          <User size={16} className="me-2 text-muted" />
          <span className="align-middle">My Profile</span>
        </DropdownItem>

        <DropdownItem
          tag={Link}
          to="/pages/account-settings"
          className="dropdown-user-item"
        >
          <Settings size={16} className="me-2 text-muted" />
          <span className="align-middle">Settings</span>
        </DropdownItem>

        <hr className="dropdown-divider dropdown-user-divider" />

        <DropdownItem tag={Link} to="/pages/faq" className="dropdown-user-item">
          <HelpCircle size={16} className="me-2 text-muted" />
          <span className="align-middle">FAQ</span>
        </DropdownItem>

        <hr className="dropdown-divider dropdown-user-divider" />

        <div className="dropdown-user-logout">
          <button
            type="button"
            className="btn btn-danger w-100 d-flex align-items-center justify-content-center"
            onClick={handleLogoutClick}
          >
            <Power size={14} className="me-1" />
            <span>Logout</span>
          </button>
        </div>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

export default UserDropdown;